import { useCallback, useEffect, useState } from "react";
import { handleError } from "../utils/errorHandling";

/**
 * General data fetching hook.
 *
 * @param fetchFunction
 * @param options
 */
export default function <T>(
  fetchFunction: () => Promise<T>,
  options: Options = {}
) {
  const [data, setData] = useState<T | undefined>();
  const [error, setError] = useState<unknown>(null);
  const [isFetching, setIsFetching] = useState(false);
  const enabled = "enabled" in options ? options.enabled : true;

  const fetchData = useCallback(async () => {
    try {
      setIsFetching(true);
      const response = await fetchFunction();
      setData(response);
      return response;
    } catch (e) {
      const normalizedError = handleError(e);
      setError(normalizedError);
    } finally {
      setIsFetching(false);
    }
  }, [fetchFunction]);

  useEffect(() => {
    if (enabled) {
      fetchData();
    }
  }, [fetchData, fetchFunction, enabled]);

  return { data, error, isFetching, refetchData: fetchData };
}

type Options = {
  enabled?: boolean;
};
